<template>
  <v-row dense>
    <v-col cols="12">
      <v-card class="chart-card" outlined>
        <v-card-title class="py-4">
          <div>
            <h3 class="font-weight-medium mb-0">Performance por Seção</h3>
            <p class="caption grey--text">
              Quantidade de cadastros por seção vinculada à equipe selecionada.
            </p>
          </div>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <template v-if="loading">
            <div class="chart-wrapper chart-wrapper--stretch">
              <v-skeleton-loader type="image"></v-skeleton-loader>
            </div>
          </template>
          <template v-else>
            <v-row dense class="pa-4 chart-block" align="stretch">
              <v-col cols="12" md="12">
                <div class="chart-wrapper chart-wrapper--stretch">
                  <div v-if="series.length">
                    <apexchart
                      type="bar"
                      :height="chartHeights.bar"
                      :options="sectionChartOptions"
                      :series="sectionChartSeries"
                    ></apexchart>
                  </div>
                  <div v-else class="empty-state">
                    Para visualizar este gráfico, selecione uma equipe.
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="12" class="chart-summary">
                <h4 class="subtitle-1 font-weight-medium mb-1">
                  Seções em destaque
                </h4>
                <p class="caption grey--text mb-3">
                  Volume consolidado por seção eleitoral.
                </p>
                <div class="summary-actions">
                  <span class="caption grey--text text--darken-1">Itens exibidos</span>
                  <v-btn-toggle
                    v-model="secaoRankingLimit"
                    dense
                    rounded
                    class="summary-toggle"
                  >
                    <v-btn small :value="10">10</v-btn>
                    <v-btn small :value="25">25</v-btn>
                    <v-btn small :value="50">50</v-btn>
                  </v-btn-toggle>
                </div>
                <v-simple-table dense v-if="topSecoes.length">
                  <tbody>
                    <tr
                      v-for="secao in topSecoes"
                      :key="secao.nome"
                      class="clickable-row"
                      @click="abrirDetalheSecao(secao.nome)"
                    >
                      <td class="summary-rank">
                        {{ secao.posicao }}º
                      </td>
                      <td class="summary-label">
                        {{ secao.nome }}
                      </td>
                      <td class="summary-value">
                        {{ formatNumber(secao.valor) }}
                      </td>
                      <td class="summary-percent">
                        {{ secao.percentual.toFixed(1) }}%
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <div v-else class="empty-state small">
                  Não há cadastros suficientes para o ranking desta seção.
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>

      <secao-detalhe-modal
        v-model="detalheDialog"
        :titulo="detalheTitulo"
        :items="detalheItems"
        :ranking="detalheRanking"
        :total="detalheTotal"
        :search.sync="detalheSearch"
      />
    </v-col>
  </v-row>
</template>

<script>
import { formatNumber, formatDateTime } from "./utils/formatters";
import SecaoDetalheModal from "./modals/SecaoDetalheModal.vue";

export default {
  name: "PerformanceSecao",
  components: {
    SecaoDetalheModal,
  },
  props: {
    series: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    pessoasMap: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartHeights: {
        bar: 450,
      },
      secaoRankingLimit: 10,
      detalheDialog: false,
      detalheTitulo: "",
      detalheSearch: "",
      detalheItems: [],
      detalheRanking: [],
      detalheTotal: 0,
    };
  },
  computed: {
    topSecoes() {
      const total = this.series.reduce((acc, item) => acc + item, 0) || 1;
      const limit = Math.min(this.secaoRankingLimit, this.labels.length || this.secaoRankingLimit);
      return this.labels
        .map((label, index) => ({
          nome: label,
          valor: this.series[index] || 0,
        }))
        .sort((a, b) => b.valor - a.valor)
        .slice(0, limit)
        .map((item, index) => ({
          ...item,
          posicao: index + 1,
          percentual: (item.valor / total) * 100,
        }));
    },
    sectionColumnWidth() {
      const total = this.series.length;
      if (!total) return "55%";
      if (total <= 3) return "40%";
      if (total <= 6) return "55%";
      if (total <= 10) return "65%";
      return "75%";
    },
    sectionChartOptions() {
      const self = this;
      return {
        chart: {
          toolbar: {
            show: false,
          },
          events: {
            dataPointSelection(event, chartContext, config) {
              if (event) {
                event.stopPropagation();
                event.preventDefault();
              }
              const index = config.dataPointIndex;
              const label = self.labels[index];
              if (label) {
                self.abrirDetalheSecao(label);
              }
            },
          },
        },
        xaxis: {
          categories: this.labels,
          labels: {
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            formatter: (val) => this.formatNumber(val),
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: this.sectionColumnWidth,
            borderRadius: 6,
          },
        },
        colors: ["#1E88E5"],
        dataLabels: {
          enabled: false,
        },
        grid: {
          padding: {
            left: 8,
            right: 8,
          },
        },
        responsive: [
          {
            breakpoint: 1264,
            options: {
              plotOptions: {
                bar: {
                  columnWidth: "60%",
                  borderRadius: 6,
                },
              },
              xaxis: {
                labels: {
                  rotate: -30,
                  style: {
                    fontSize: "12px",
                  },
                },
              },
            },
          },
          {
            breakpoint: 960,
            options: {
              plotOptions: {
                bar: {
                  columnWidth: "65%",
                  borderRadius: 8,
                },
              },
              xaxis: {
                labels: {
                  rotate: -30,
                  style: {
                    fontSize: "11px",
                  },
                },
              },
            },
          },
          {
            breakpoint: 600,
            options: {
              plotOptions: {
                bar: {
                  columnWidth: "75%",
                  borderRadius: 10,
                },
              },
              xaxis: {
                labels: {
                  rotate: -25,
                  style: {
                    fontSize: "10px",
                  },
                },
              },
            },
          },
        ],
      };
    },
    sectionChartSeries() {
      return [
        {
          name: "Cadastros",
          data: this.series,
        },
      ];
    },
  },
  watch: {
    detalheDialog(valor) {
      if (!valor) {
        this.detalheSearch = "";
        this.detalheItems = [];
        this.detalheTitulo = "";
        this.detalheRanking = [];
        this.detalheTotal = 0;
      }
    },
  },
  methods: {
    formatNumber,
    formatDateTime,
    abrirDetalheSecao(label) {
      if (!label) {
        return;
      }

      if (!this.pessoasMap || !Object.keys(this.pessoasMap).length) {
        return;
      }

      const normalizedLabel = String(label);
      let items = [];

      if (this.pessoasMap[normalizedLabel]) {
        items = this.pessoasMap[normalizedLabel];
      }

      this.detalheItems = items;
      this.detalheRanking = this.calcularRankingAssessores(items);
      this.detalheTotal = items.length;
      this.detalheTitulo = normalizedLabel;
      this.detalheSearch = "";

      setTimeout(() => {
        this.detalheDialog = true;
      }, 100);
    },
    calcularRankingAssessores(items) {
      if (!items || !items.length) {
        return [];
      }

      const total = items.length || 1;
      const mapa = {};

      items.forEach((item) => {
        const assessor = item.assessor || "Não informado";
        mapa[assessor] = (mapa[assessor] || 0) + 1;
      });

      return Object.entries(mapa)
        .map(([nome, qtd]) => ({
          nome,
          total: qtd,
          percentual: (qtd / total) * 100,
        }))
        .sort((a, b) => b.total - a.total)
        .map((item, index) => ({
          ...item,
          posicao: index + 1,
        }));
    },
  },
};
</script>

<style scoped>
.chart-card {
  border-radius: 16px;
  min-height: 140px;
}

.chart-block {
  min-height: 360px;
}

.chart-wrapper {
  min-height: 360px;
  width: 100%;
}

.chart-wrapper--stretch {
  min-height: 400px;
  width: 100%;
}

.chart-summary {
  display: flex;
  flex-direction: column;
}

.summary-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.summary-toggle {
  background-color: #eceff1;
}

.summary-rank {
  width: 50px;
  font-weight: 600;
  color: #1976d2;
}

.summary-label {
  font-weight: 500;
  white-space: nowrap;
}

.summary-value {
  text-align: right;
  font-weight: 500;
}

.summary-percent {
  text-align: right;
  font-size: 0.85rem;
  color: #616161;
  width: 65px;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.empty-state {
  text-align: center;
  max-width: 320px;
  line-height: 1.4;
  color: #757575;
}

.empty-state.small {
  max-width: none;
  text-align: left;
}

@media (max-width: 960px) {
  .chart-card {
    min-height: 320px;
  }
  .chart-block {
    min-height: 320px;
  }
  .chart-wrapper {
    min-height: 280px;
  }
  .chart-wrapper--stretch {
    min-height: 320px;
  }
}
</style>
